import { CSSProperties, FC, ReactNode, useMemo } from 'react';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';

import { SQL_RESERVED_WORDS } from './VantageCodeEditorModal/helpers/query';

type VantageCodeLanguage = 'cql' | 'json' | 'sql';

interface VantageCodeBlockProps {
    code: string;
    language: VantageCodeLanguage;
    style?: CSSProperties;
}

type HighlightColors = {
    boolean: string;
    comment: string;
    key: string;
    keyword: string;
    null: string;
    number: string;
    punctuation: string;
    string: string;
    variable: string;
};

const jsonTokenPattern =
    /("(?:\\u[\da-fA-F]{4}|\\[^u]|[^\\"])*"|true|false|null|-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?|[{}\[\],:])/g;

const sqlKeywordPattern = SQL_RESERVED_WORDS.join('|');
const sqlTokenPattern = new RegExp(
    `(--[^\\n]*|\\/\\*[\\s\\S]*?\\*\\/|{{[\\s\\S]*?}}|'(?:''|[^'])*'|"(?:""|[^"])*"|\\b(?:${sqlKeywordPattern})\\b|-?\\d+(?:\\.\\d+)?(?:[eE][+-]?\\d+)?|<>|!=|<=|>=|::|[(),;.*+\\-/=<>])`,
    'gi',
);

const renderHighlightedJson = (json: string, colors: HighlightColors): ReactNode[] => {
    const tokens: ReactNode[] = [];
    let lastIndex = 0;
    let tokenIndex = 0;
    let match: RegExpExecArray | null;

    jsonTokenPattern.lastIndex = 0;

    while ((match = jsonTokenPattern.exec(json))) {
        const [value] = match;
        const matchIndex = match.index;

        if (matchIndex > lastIndex) {
            tokens.push(json.slice(lastIndex, matchIndex));
        }

        const trailingText = json.slice(jsonTokenPattern.lastIndex);
        const isKey = value.startsWith('"') && /^\s*:/.test(trailingText);
        const color = isKey
            ? colors.key
            : value.startsWith('"')
              ? colors.string
              : value === 'true' || value === 'false'
                ? colors.boolean
                : value === 'null'
                  ? colors.null
                  : /^-?\d/.test(value)
                    ? colors.number
                    : colors.punctuation;

        tokens.push(
            <span key={`${tokenIndex}-${matchIndex}`} style={{ color }}>
                {value}
            </span>,
        );
        tokenIndex += 1;
        lastIndex = jsonTokenPattern.lastIndex;
    }

    if (lastIndex < json.length) {
        tokens.push(json.slice(lastIndex));
    }

    return tokens;
};

const renderHighlightedSql = (sql: string, colors: HighlightColors): ReactNode[] => {
    const tokens: ReactNode[] = [];
    let lastIndex = 0;
    let tokenIndex = 0;
    let match: RegExpExecArray | null;

    sqlTokenPattern.lastIndex = 0;

    while ((match = sqlTokenPattern.exec(sql))) {
        const [value] = match;
        const matchIndex = match.index;

        if (matchIndex > lastIndex) {
            tokens.push(sql.slice(lastIndex, matchIndex));
        }

        const normalizedValue = value.toLowerCase();
        const color =
            value.startsWith('--') || value.startsWith('/*')
                ? colors.comment
                : value.startsWith('{{')
                  ? colors.variable
                  : value.startsWith("'") || value.startsWith('"')
                    ? colors.string
                    : SQL_RESERVED_WORDS.includes(normalizedValue)
                      ? colors.keyword
                      : /^-?\d/.test(value)
                        ? colors.number
                        : colors.punctuation;

        tokens.push(
            <span key={`${tokenIndex}-${matchIndex}`} style={{ color }}>
                {value}
            </span>,
        );
        tokenIndex += 1;
        lastIndex = sqlTokenPattern.lastIndex;
    }

    if (lastIndex < sql.length) {
        tokens.push(sql.slice(lastIndex));
    }

    return tokens;
};

const VantageCodeBlock: FC<VantageCodeBlockProps> = ({ code, language, style }) => {
    const { token } = useAntDesignTheme();
    const highlightedCode = useMemo(() => {
        const colors: HighlightColors = {
            boolean: token.colorWarning,
            comment: token.colorTextTertiary,
            key: token.colorPrimary,
            keyword: token.colorPrimary,
            null: token.colorTextTertiary,
            number: token.colorSuccess,
            punctuation: token.colorTextTertiary,
            string: token.colorTextSecondary,
            variable: token.colorInfo,
        };

        return language === 'json' ? renderHighlightedJson(code, colors) : renderHighlightedSql(code, colors);
    }, [
        code,
        language,
        token.colorInfo,
        token.colorPrimary,
        token.colorSuccess,
        token.colorTextSecondary,
        token.colorTextTertiary,
        token.colorWarning,
    ]);

    return (
        <pre
            style={{
                backgroundColor: token.colorBgBase,
                color: token.colorText,
                fontFamily: token.fontFamilyCode,
                fontSize: 12,
                height: '100%',
                lineHeight: 1.65,
                margin: 0,
                maxWidth: '100%',
                overflow: 'auto',
                overflowWrap: 'anywhere',
                padding: '1rem',
                tabSize: 2,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                ...style,
            }}
        >
            <code style={{ whiteSpace: 'inherit' }}>{highlightedCode}</code>
        </pre>
    );
};

export default VantageCodeBlock;
